import React from "react";
import { Button } from "../../atoms";
import { FormHelpCallout } from "../FormHelpCallout";
import {
  formatSegmentDateRange,
  type SegmentChain,
} from "../../../utils/chart/segment-grouping.utils";
import { getOpenSegmentTarget } from "../../../utils/chart/line-item-revision.utils";

export interface RevisionHistoryPanelProps {
  chain: SegmentChain;
  onClose: () => void;
  onViewSegment: (segmentId: string, kind: SegmentChain["kind"]) => void;
  onReviseAgain?: () => void;
  onUndoLatest?: (latestRevisionId: string) => void;
  isUndoing?: boolean;
}

export const RevisionHistoryPanel: React.FC<RevisionHistoryPanelProps> = ({
  chain,
  onClose,
  onViewSegment,
  onReviseAgain,
  onUndoLatest,
  isUndoing = false,
}) => {
  const openSegment = getOpenSegmentTarget(
    {
      id: chain.segments[chain.segments.length - 1]?.id ?? chain.groupId,
      lineItemGroupId: chain.groupId,
      startDate: chain.segments[chain.segments.length - 1]?.startDate,
      endDate: chain.segments[chain.segments.length - 1]?.endDate,
    },
    chain.segments.map((segment) => ({
      id: segment.id,
      lineItemGroupId: chain.groupId,
      startDate: segment.startDate,
      endDate: segment.endDate,
    })),
  );

  const latestRevision =
    openSegment?.id &&
    chain.segments.find(
      (segment) =>
        segment.id === openSegment.id && segment.segmentKind === "revision",
    );

  return (
    <div className="space-y-4">
      <FormHelpCallout>
        <strong>{chain.label}</strong> has {chain.segments.length} segments.
        Past months use the segment active for that date; only the open segment
        can be revised or undone.
      </FormHelpCallout>

      <div className="space-y-2">
        {chain.segments.map((segment, index) => {
          const isOpen = openSegment?.id === segment.id;
          return (
            <div
              key={segment.id}
              className={`rounded-[14px] border px-4 py-3 ${
                isOpen
                  ? "border-brand/30 bg-brand/10"
                  : "border-white/10 bg-white/[0.03]"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-white">
                      Segment {index + 1}
                    </span>
                    <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white/70">
                      {segment.segmentKind ?? "initial"}
                    </span>
                    {isOpen ? (
                      <span className="rounded-full bg-brand/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-200">
                        current
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 text-sm text-white/70">
                    {formatSegmentDateRange(segment.startDate, segment.endDate)}
                  </p>
                  <p className="mt-1 text-sm font-medium text-white">
                    {segment.amountLabel}
                  </p>
                  {segment.revisionNote ? (
                    <p className="mt-1 text-xs text-white/50">
                      {segment.revisionNote}
                    </p>
                  ) : null}
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => onViewSegment(segment.id, chain.kind)}
                >
                  View
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2 pt-2">
        {onReviseAgain ? (
          <Button type="button" variant="primary" size="sm" onClick={onReviseAgain}>
            Revise again
          </Button>
        ) : null}
        {latestRevision && onUndoLatest ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={isUndoing}
            onClick={() => {
              if (
                window.confirm(
                  "Undo the latest revision? The previous segment will become current again.",
                )
              ) {
                onUndoLatest(latestRevision.id);
              }
            }}
          >
            {isUndoing ? "Undoing..." : "Undo latest revision"}
          </Button>
        ) : null}
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
};

export default RevisionHistoryPanel;
